import React, {lazy, memo, Suspense, useEffect, useMemo, useRef, useState} from "react"
import {Button, Image, Tab} from "semantic-ui-react"
import {Navigate, Route, Routes,} from "react-router-dom"
import {analyticsEvent} from "./utils/google-analytics"
import queryString, {ParsedQuery} from "query-string"
import {Details} from "./details/details"
import {useIntl} from "react-intl"
import {IndiInfo} from "./topola"
import {TopBar} from "./menu/top-bar"
import {useLocation, useNavigate} from "react-router"
import {ChartType, TopolaChart} from "./chart"
import {getSelection} from "./datasource/load-data"
import {GedcomLanguage as Language} from "./model/gedcom-language"
import {configToArgs} from "./utils/config-utils"
import LanguagesLoader from "./datasource/load-languages"
import {getArguments} from "./utils/param-utils"
import {downloadGedcom, downloadPng, downloadSvg, getFilename} from "./utils/chart-utils"
import {Config, EthnicityArg, IdsArg, LanguagesArg, SexArg, ConfigPanel, DEFAULT_CONFIG} from "./config"
import {LandingPage} from "./landing/landing"
import {exportXlsx, importXlsx} from "./utils/xlsx-utils"
import {I18nIndividual as GedcomIndividual} from "./model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "./model/gedcom-family"
import {ErrorBoundary, ErrorBoundaryChecker} from "./error/error-boundary"
import {ErrorPage} from "./error/error-page"
import {useWithSentryReport} from "./error/sentry-feedback"
import {ErrorPopup} from "./error/error-popup"
import {creatorToGedcom, toChart} from "./converter/create-gedcom-backend"
import {gedcomToCreator} from "./converter/edit-gedcom-backend"
import {CreatorProgressBar, ProgressProvider} from "./creator/creator-progress"
import {HowItWorks} from "./howitworks/howitworks"
import {Media, ScreenSize} from "./utils/media-utils"
import {CheckoutSuccess} from "./api/checkout-success"
import {CheckoutCancelled} from "./api/checkout-cancelled"
import {validateKey} from "./api/api"
import {PricingModal} from "./pricing"
import {CreatorContext} from "./creator/creator"
import {
    DataSourceEnum,
    DataSourceSpecs,
    EditSourceSpec,
    GedcomEditDataSource,
    SourceSelection,
    UploadedDataSource,
    UploadSourceSpec
} from "./datasource/data-source-specs"
import {
    getEthnicities,
    idToIndiMap,
    jsonToGedcom,
    loadLanguageOptions,
    startIndi,
    TopolaData
} from "./utils/gedcom-utils"
import * as Sentry from "@sentry/react"


export enum AppState {
    INITIAL,
    LOADING,
    SHOWING_CHART,
    ERROR
}

interface AppProps { setI18nLanguage: (lang: string) => void }

export function App(props: AppProps) {
    const onI18nLanguage = props.setI18nLanguage
    const location = useLocation()
    const navigate = useNavigate()
    const intl = useIntl()

    const [appState, setAppState] = useState(AppState.INITIAL)
    const [allLanguages, setAllLanguages] = useState<Language[]>([])
    const [sourceSpec, setSourceSpec] = useState<DataSourceSpecs>()
    const [topolaData, setTopolaData] = useState<TopolaData>()
    const [gedcomString, setGedcomString] = useState<string>()

    const [config, setConfig] = useState(DEFAULT_CONFIG)
    const [selection, setSelection] = useState<IndiInfo>()
    const [showSidePanel, setShowSidePanel] = useState(false)
    const [chartType, setChartType] = useState(ChartType.Hourglass)
    const [freezeAnimation, setFreezeAnimation] = useState(false)

    const [error, setError] = useState<string | Error>()
    const [errorBoundaryCheck, setErrorBoundaryCheck] = useState(false)
    const [showErrorPopup, setShowErrorPopup] = useState(false)
    const withCrashReport = useWithSentryReport()

    const TopMenu = memo(TopBar)
    const CreatorTabs = lazy(() => import("./creator/creator").then(module => ({ default: module.CreatorTabs })))

    const [loading, setLoading] = useState(true)
    const [allowConvert, setAllowConvert] = useState(false)
    const [contactModalOpen, setContactModalOpen] = useState(false)
    const [pricingModalOpen, setPricingModalOpen] = useState(false)
    const [checkoutSuccess, setCheckoutSuccess] = useState(false)
    const [privacyModalOpen, setPrivacyModalOpen] = useState(false)
    const [howItWorksOpen, setHowItWorksOpen] = useState(false)

    const [creatorKey, setCreatorKey] = useState(0)
    const creatorDataRef = useRef<{ individuals: GedcomIndividual[], families: GedcomFamily[] }>({
        individuals: [],
        families: []
    })
    const [unsavedChanges, setUnsavedChanges] = useState(false)
    const creatorContextValue = useMemo(() => ({creatorDataRef}), [])
    const creatorEventHandlers = useMemo(() => ({
        setUnsavedChanges,
        setLoading,
        setAllowConvert,
        setContactModalOpen,
    }), [setUnsavedChanges, setLoading, setAllowConvert, setContactModalOpen])

    useEffect(() => {
        analyticsEvent("landing")
        loadLanguages().catch(e => console.error("Failed to load languages:", e))
        loadDataFromArgs().catch(e => console.error("Failed to load data:", e))
    })

    /**
     * Stop spinner at arriving to /view
     */
    useEffect(() => { if (appState === AppState.SHOWING_CHART && topolaData) setLoading(false) }, [appState, topolaData])

    async function loadLanguages() {
        try {
            const allLanguages = await LanguagesLoader.loadLanguages("data/languages/languages.csv") || []
            setAllLanguages(allLanguages)
        } catch (e) {
            console.error("Failed to load languages:", e)
        }
    }

    const uploadedDataSource = new UploadedDataSource()
    const gedcomEditDataSource = new GedcomEditDataSource()

    function isNewData(newSourceSpec: DataSourceSpecs, newSelection?: IndiInfo) {
        if (!sourceSpec || sourceSpec.source !== newSourceSpec.source) return true  // New data source means new data
        const newSource = {spec: newSourceSpec, selection: newSelection}
        const oldSource = {spec: sourceSpec, selection: selection}
        switch (newSource.spec.source) {
            case DataSourceEnum.UPLOADED:
                return uploadedDataSource.isNewData(newSource as SourceSelection<UploadSourceSpec>, oldSource as SourceSelection<UploadSourceSpec>, topolaData)
            case DataSourceEnum.EDIT:
                return gedcomEditDataSource.isNewData(newSource as SourceSelection<EditSourceSpec>, oldSource as SourceSelection<EditSourceSpec>, topolaData)
        }
    }

    function loadData(newSourceSpec: DataSourceSpecs, allLanguages: Language[], newSelection?: IndiInfo) {
        switch (newSourceSpec.source) {
            case DataSourceEnum.UPLOADED:
                return uploadedDataSource.loadData({spec: newSourceSpec, selection: newSelection, allLanguages: allLanguages})
            case DataSourceEnum.EDIT:
                return gedcomEditDataSource.loadData({spec: newSourceSpec, selection: newSelection, allLanguages: allLanguages})
        }
    }

    async function loadDataFromArgs() {
        if (location.pathname !== "/view") {
            appState !== AppState.INITIAL && setAppState(AppState.INITIAL)
            return
        }
        const args = getArguments(location, allLanguages)
        if (!args.sourceSpec) {
            navigate("/")
            return
        }
        if (appState === AppState.INITIAL || isNewData(args.sourceSpec, args.selection)) {
            try {
                setAppState(AppState.LOADING)
                setSourceSpec(args.sourceSpec)
                setChartType(args.chartType)
                setFreezeAnimation(args.freezeAnimation)
                setConfig(args.config)
                const data = await loadData(args.sourceSpec!, allLanguages, args.selection)
                sessionStorage.setItem("filename", getFilename(data.gedcom))
                setTopolaData(data)
                setGedcomString(jsonToGedcom(data.gedcom))
                setSelection(args.selection !== undefined ? args.selection : startIndi(data))
                setShowSidePanel(args.showSidePanel)
                setAppState(AppState.SHOWING_CHART)
                toggleDetails(args.config, data, allLanguages)
            } catch (error: any) {
                setError(error)
                setAppState(AppState.ERROR)
            }
        } else if (appState === AppState.SHOWING_CHART || appState === AppState.LOADING) {
            setChartType(args.chartType)
            setAppState(AppState.SHOWING_CHART)
            updateDisplay(args.selection !== undefined ? args.selection : startIndi(topolaData))
        }
    }

    function toggleDetails(config: Config, data: TopolaData | undefined, allLanguages: Language[]) {
        if (data === undefined) return
        // Set up if there are languages
        config.languageOptions = loadLanguageOptions(data, allLanguages)
        config.renderLanguagesOption = config.languageOptions.length > 0
        // Set up if there are ethnicities/tribes
        config.renderEthnicityOption = Array.from(getEthnicities(data)).length > 0
        idToIndiMap(data.chartData).forEach((indi) => {
            indi.hideLanguages = config.languages === LanguagesArg.HIDE
            indi.hideEthnicity = config.ethnicity === EthnicityArg.HIDE
            indi.hideId = config.id === IdsArg.HIDE
            indi.hideSex = config.sex === SexArg.HIDE
        })
    }

    /**
     * Sets the state with a new individual selection and chart type.
     */
    function updateDisplay(newSelection: IndiInfo) {
        if (!selection || selection.id !== newSelection.id || selection!.generation !== newSelection.generation)
            setSelection(newSelection)
    }

    function updateUrl(args: ParsedQuery<any>) {
        const search = queryString.parse(location.search)
        for (const key in args) search[key] = args[key]
        const path = location.pathname + "?" + queryString.stringify(search)
        navigate(path, { state: location.state })
    }

/* ------------------------------------------------------------------------------------------------------------------ */
/* EVENT HANDLERS                                                                                                     */
/* ------------------------------------------------------------------------------------------------------------------ */

    /**
     * Called if the user clicks an individual box in the chart. Updates browser URL.
     */
    const onSelection = (selection: IndiInfo) => updateUrl({indi: selection.id, gen: selection.generation})

    const onDownloadPng = withCrashReport(async() => {
        const filename = getFilename(topolaData?.gedcom)
        await downloadPng(filename)
        analyticsEvent("download_png")
    }, { id: "error.failed.download.png" })

    const onDownloadSvg = withCrashReport(async() => {
        const filename = getFilename(topolaData?.gedcom)
        await downloadSvg(intl, filename)
        analyticsEvent("download_svg")
    }, { id: "error.failed.download.svg" })

    const onDownloadGedcom = withCrashReport(async() => {
        const filename = getFilename(topolaData?.gedcom)
        await downloadGedcom(intl, gedcomString as string, filename)
        analyticsEvent("download_gedcom")
    }, { id: "error.failed.download.gedcom" })

    const onResetView = () => {
        const i = startIndi(topolaData)
        const args = {indi: i.id, gen: i.generation}
        const search = queryString.parse(location.search)
        for (const key in args) delete search[key]
        location.search = queryString.stringify(search)
        navigate(location.pathname + "?" + queryString.stringify(search), { state: location.state, replace: true })
        analyticsEvent("reset_view")
    }

    const onEditGedcom = withCrashReport(() => {
        setLoading(true)
        const { individualsData, familiesData } = gedcomToCreator(gedcomString as string, allLanguages)
        navigate("/create", { state: { individualsData, familiesData, from: "/view" } })
        console.info("Loading Creator...")
    }, { id: "error.failed.edit.gedcom" }, () => setLoading(false))

    const onImportXlsx = withCrashReport(async(file: File, event: string) => {
        setLoading(true)
        const { individualsData, familiesData, metadata } = await importXlsx(file, allLanguages, intl)
        creatorDataRef.current = { individuals: individualsData, families: familiesData }
        setCreatorKey(metadata.version + 1)
        navigate("/create", { state: { individualsData, familiesData, from: "import" }})
        analyticsEvent(event)
    }, { id: "error.failed.import.xlsx" })

    const onExportXlsx = withCrashReport(async() => {
        if (await gateToCheckout()) {
            goToCheckout("onExportXlsx")
            return  // Block and go to pricing 🚫
        }
        const { individuals, families } = creatorDataRef.current
        // Filename not available from React state across different tabs
        const filename = (sessionStorage.getItem("filename") ?? intl.formatMessage({ id: "generic.file.name" })) + ".xlsx"
        await exportXlsx(individuals, families, creatorKey, intl, filename, true)
        setUnsavedChanges(false)
        analyticsEvent("export_xlsx")
    }, { id: "error.failed.download.xlsx" })

    const onConvert = withCrashReport(async() => {
        if (await gateToCheckout()) {
            goToCheckout("onConvert")
            return  // Block and go to pricing 🚫
        }
        setLoading(true)
        const {gedcomContent, filename} = await creatorToGedcom(creatorDataRef.current.individuals, creatorDataRef.current.families, intl)
        const file = new File([gedcomContent], filename)
        await toChart(file, navigate)
        analyticsEvent("gedcom_convert")
    }, { id: "error.failed.download.gedcom" }, () => setLoading(false))

    const gateToCheckout = async () => {
        try {
            if (creatorDataRef.current.individuals.length <= Number(process.env.REACT_APP_CREATOR_TRIAL)) return false
            const sessionId = localStorage.getItem("sessionId")
            if (!sessionId) return true
            const response = await validateKey(undefined, sessionId)
            return !response.creatorKey
        } catch (e: any) {
            console.error("Error validating session:", e)
            Sentry.captureException(e, { extra: { context: "Error validating session" }})
            setPricingModalOpen(true)
            return true  // Block and go to pricing
        }
    }

    const goToCheckout = (feature: string) => {
        console.log("Redirecting to checkout...")
        Sentry.captureMessage("↪️ Redirecting to checkout", {level: "log", extra: { feature: feature }})
        setPricingModalOpen(true)
    }

/* ------------------------------------------------------------------------------------------------------------------ */
/* ERROR HANDLING                                                                                                     */
/* ------------------------------------------------------------------------------------------------------------------ */

    /**
     * Debounces error capturing and shows a popup in the middle of the screen.
     */
    useEffect(() => {
        if (appState === AppState.ERROR && error) withCrashReport(() => { throw error }, { id: "error.unknown" })()
    }, [appState, error, withCrashReport])

/* ------------------------------------------------------------------------------------------------------------------ */

    /**
     * [VIEW CHART]
     */
    const renderView = () => {
        switch (appState) {
            case AppState.LOADING:
            case AppState.SHOWING_CHART:
                const updatedSelection = getSelection(topolaData!.chartData, selection)
                const sidePanel = (updatedSelection: IndiInfo) => [{
                    key: "info",
                    menuItem: { icon: "info circle", className: "config-tab-info", content: intl.formatMessage({ id: "tab.info" })},
                    render: () => <Details gedcom={topolaData!.gedcom} indi={updatedSelection.id}/>
                }, {
                    key: "settings",
                    menuItem: { icon: "setting", className: "config-tab-settings", content: intl.formatMessage({ id: "tab.settings" })},
                    render: () => <ConfigPanel config={config} onChange={(config) => {
                        setConfig(config)
                        toggleDetails(config, topolaData, allLanguages)
                        updateUrl(configToArgs(config))
                    }}/>
                }]
                return (
                    <div id="content">
                        <TopolaChart
                            data={topolaData!.chartData}
                            selection={updatedSelection}
                            chartType={chartType}
                            freezeAnimation={freezeAnimation}
                            colors={config.color}
                            selectedLanguage={config.selectedLanguage}
                            hideLanguages={config.languages}
                            hideEthnicity={config.ethnicity}
                            hideIds={config.id}
                            hideSex={config.sex}
                            onSelection={onSelection}
                            languageOptions={config.languageOptions}
                        />
                        {/* DONATSO CHART */}
                        {/* <DonatsoChart data={data!.chartData} selection={selection!} onSelection={onSelection}/> */}
                        {showSidePanel ? <Media greaterThanOrEqual="desktop" className="sidePanel">
                            <Tab panes={sidePanel(updatedSelection)}/>
                        </Media> : null}
                    </div>
                )
            case AppState.ERROR:
                setShowErrorPopup(true)
        }
    }

    /**
     * [MENU BAR]
     * [LANDING]
     * [CREATOR]
     * [VIEW]
     * [PAYMENTS]
     */
    return (
        <>
            <ErrorBoundary key={errorBoundaryCheck.toString()} fallback={useMemo(() => <ErrorPage screenSize={ScreenSize.LARGE} onBackHome={() => {
                setErrorBoundaryCheck(false)
                navigate("/")
            }}/>, [navigate])}>
                <CreatorContext.Provider value={creatorContextValue}>
                    <ProgressProvider>

                        <TopMenu
                            appState={appState}
                            searchData={topolaData?.chartData}
                            howItWorksOpen={howItWorksOpen}
                            unsavedChanges={unsavedChanges}
                            contactModalOpen={contactModalOpen}
                            pricingModalOpen={pricingModalOpen}
                            checkoutSuccess={checkoutSuccess}
                            privacyModalOpen={privacyModalOpen}
                            allowConvert={allowConvert}
                            loading={loading}
                            eventHandlers={{
                                onHome: () => navigate("/"),
                                // Top bar
                                setContactModalOpen, setPricingModalOpen, setPrivacyModalOpen, onI18nLanguage,
                                // View
                                onSelection, onDownloadPng, onDownloadSvg, onDownloadGedcom, onResetView, onEditGedcom,
                                // Creator
                                setHowItWorksOpen, onImportXlsx, onExportXlsx, onConvert,
                                // Payment
                                setCheckoutSuccess
                            }}
                        />

                        <ErrorPopup open={showErrorPopup} message={error} onDismiss={() => setShowErrorPopup(false)}/>

                        <Routes>
                            <Route path="/" element={<>
                                <LandingPage onCreatorExample={onImportXlsx}/>
                                {/* Dev ErrorBoundaryChecker */}
                                <Button
                                    id="error-boundary"
                                    className={process.env.REACT_APP_SHOW_ERROR_BOUNDARY === "false" ? "hidden": ""}
                                    onClick={() => setErrorBoundaryCheck(true)}>
                                    <span>Check &lt;ErrorBoundary/&gt;</span>
                                </Button>
                                {errorBoundaryCheck && <ErrorBoundaryChecker/>}
                            </>}/>
                            <Route path="/create" element={
                                <>
                                    <CreatorProgressBar/>
                                    {/* Lazy load */}
                                    <Suspense fallback={<div>{/* ... */}</div>}>
                                        <HowItWorks open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} setContactModalOpen={setContactModalOpen}/>
                                        <PricingModal
                                            screenSize={ScreenSize.LARGE}
                                            open={pricingModalOpen}
                                            onClose={() => setPricingModalOpen(false)}
                                            checkoutSuccess={checkoutSuccess}
                                            setCheckoutSuccess={setCheckoutSuccess}
                                            setContactModalOpen={setContactModalOpen}
                                        />
                                        <Image id="creator-banner" alt="creator-banner" src="/assets/families-banner.svg"/>
                                        <CreatorTabs key={creatorKey} eventHandlers={creatorEventHandlers}/>
                                    </Suspense>
                                </>
                            }/>
                            <Route path="/view" element={<>{renderView()}</>}/>
                            <Route path="/success" element={<CheckoutSuccess
                                setCheckoutSuccess={setCheckoutSuccess}
                                setPricingModalOpen={setPricingModalOpen}
                            />}/>
                            <Route path="/cancel" element={<CheckoutCancelled/>}/>
                            <Route path="*" element={<Navigate to="/" replace/>}/>
                        </Routes>

                    </ProgressProvider>
                </CreatorContext.Provider>
            </ErrorBoundary>
        </>
    )
}
